export type FunctionResult = { output: boolean } | { error: string };
export interface FunctionRunner {
  submit(key: object, code: string, input: boolean, complete: (result: FunctionResult) => void): boolean;
  cancel(key: object): void;
  dispose(): void;
}
interface Job {
  id: number;
  code: string;
  input: boolean;
  complete: (result: FunctionResult) => void;
}

// User code only receives a boolean. The opaque iframe creates a CSP-restricted
// worker; neither it nor that worker receives editor objects or project data.
const workerSource = `
self.postMessage({ready:true});
self.onmessage = ({data}) => {
  self.postMessage({id:data.id, started:true});
  try {
    const output = new Function('input', '"use strict";\\n' + data.code)(data.input);
    if (typeof output !== 'boolean') throw new Error('Return a boolean: true or false.');
    self.postMessage({id:data.id, output});
  } catch (error) {
    self.postMessage({id:data.id, error:String(error && error.message || error).slice(0,300)});
  }
};`;
const bridgeSource = `
let worker, token;
function start() {
  if (worker) worker.terminate();
  const url = URL.createObjectURL(new Blob([${JSON.stringify(workerSource)}], {type:'text/javascript'}));
  worker = new Worker(url);
  URL.revokeObjectURL(url);
  worker.onmessage = ({data}) => parent.postMessage({token, ...data}, '*');
  worker.onerror = () => parent.postMessage({token, failure:true}, '*');
}
addEventListener('message', ({source,data}) => {
  if (source !== parent) return;
  if (!token) token = data.token;
  if (data.token !== token) return;
  if (data.reset) start();
  else if (worker) worker.postMessage(data.job);
});`;

/** One worker, at most 128 pending nodes; newer inputs replace pending work. */
export function createFunctionRunner(): FunctionRunner {
  const token = crypto.randomUUID();
  const frame = document.createElement('iframe');
  frame.hidden = true;
  frame.title = 'JavaScript function runtime';
  frame.setAttribute('sandbox', 'allow-scripts');
  frame.srcdoc = `<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline' 'unsafe-eval' blob:; worker-src blob:; connect-src 'none'"><script>${bridgeSource}</script>`;
  const pending = new Map<object, Job>();
  let active: { key: object; job: Job; started: boolean } | undefined;
  let ready = false;
  let disposed = false;
  let unavailable: string | undefined;
  let nextId = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const post = (data: object) => frame.contentWindow?.postMessage({ token, ...data }, '*');
  const pump = () => {
    if (!ready || active || disposed || !pending.size) return;
    const [key, job] = pending.entries().next().value!;
    pending.delete(key);
    active = { key, job, started: false };
    post({ job: { id: job.id, code: job.code, input: job.input } });
    timer = setTimeout(() => fail('JavaScript worker did not accept the function within 5 seconds.'), 5000);
  };
  const reset = () => {
    ready = false;
    post({ reset: true });
    timer = setTimeout(() => fail('Unable to start the JavaScript worker.'), 5000);
  };
  const fail = (error: string) => {
    clearTimeout(timer);
    const job = active?.job;
    active = undefined;
    if (!ready) {
      // A blocked worker must not produce a restart loop.
      unavailable = error;
      for (const queued of pending.values()) queued.complete({ error });
      pending.clear();
    }
    job?.complete({ error });
    if (ready && !disposed) reset();
  };
  const receive = (event: MessageEvent) => {
    if (disposed || event.source !== frame.contentWindow || event.data?.token !== token) return;
    const data = event.data;
    if (data.ready === true && !ready) {
      clearTimeout(timer);
      ready = true;
      pump();
    } else if (data.failure) fail('JavaScript worker failed. Disable and enable live I/O to retry.');
    else if (active && data.id === active.job.id) {
      if (data.started === true) {
        if (!active.started) {
          active.started = true;
          clearTimeout(timer);
          timer = setTimeout(() => fail('Function exceeded the 100 ms execution limit.'), 100);
        }
        return;
      }
      clearTimeout(timer);
      const { job } = active;
      active = undefined;
      job.complete(typeof data.output === 'boolean'
        ? { output: data.output }
        : { error: typeof data.error === 'string' ? data.error.slice(0, 300) : 'Invalid function result.' });
      pump();
    }
  };
  window.addEventListener('message', receive);
  frame.onload = () => { if (!disposed) reset(); };
  document.body.append(frame);
  return {
    submit(key, code, input, complete) {
      if (unavailable && !disposed) { complete({ error: unavailable }); return true; }
      if (disposed || (!pending.has(key) && pending.size >= 128)) return false;
      pending.set(key, { id: ++nextId, code, input, complete });
      pump();
      return true;
    },
    cancel(key) {
      if (disposed) return;
      pending.delete(key);
      if (active?.key === key) {
        clearTimeout(timer);
        active = undefined;
        reset();
      }
    },
    dispose() {
      disposed = true;
      clearTimeout(timer);
      pending.clear();
      active = undefined;
      window.removeEventListener('message', receive);
      frame.remove();
    },
  };
}
